<template>
  <div class="space-y-8">
    <!-- Header với thống kê -->
    <BusinessDataHeader 
      :stats="stats" 
      @add-faq="showFaqModal = true"
      @add-data-item="showItemModal = true"
      @refresh="loadData"
    />
    
    <!-- FAQ Section -->
    <FaqSection 
      :faqs="faqs" 
      :categories="faqCategories"
      :pagination="faqPagination"
      :is-loading="isLoading"
      :is-searching="isSearching"
      @search="onFaqSearch"
      @filter-change="onFaqFilterChange"
      @page-change="onFaqPageChange"
      @refresh="loadData"
      @add-faq="showFaqModal = true"
      @edit-faq="editFaq"
    />
    
    <!-- Data Items Section -->
    <DataItemsSection 
      :data-items="dataItems"
      :categories="itemCategories"
      :pagination="itemPagination"
      :is-loading="isLoading"
      :is-searching="isSearching"
      @search="onItemSearch"
      @filter-change="onItemFilterChange"
      @page-change="onItemPageChange"
      @refresh="loadData"
      @add-data-item="showItemModal = true"
      @edit-data-item="editDataItem"
    />
    
    <!-- FAQ Modal -->
    <FaqModal
      :show="showFaqModal"
      :faq="editingFaq"
      :categories="faqCategories"
      :business-categories="businessFaqCategories"
      @close="closeFaqModal"
      @saved="onFaqSaved"
      @category-created="onCategoryCreated"
    />
    
    <!-- Data Item Modal -->
    <DataItemModal
      :show="showItemModal"
      :data-item="editingItem"
      :categories="itemCategories"
      :business-categories="businessDataCategories"
      @close="closeItemModal"
      @saved="onItemSaved"
      @category-created="onCategoryCreated"
    />
  </div>
</template>

<script>
import { useAlert } from 'dashboard/composables';
import BusinessDataHeader from './BusinessDataHeader.vue';
import FaqSection from './FaqSection.vue';
import DataItemsSection from './DataItemsSection.vue';
import FaqModal from './FaqModal.vue';
import DataItemModal from './DataItemModal.vue';

export default {
  name: 'BusinessDataTab',
  
  components: {
    BusinessDataHeader,
    FaqSection,
    DataItemsSection,
    FaqModal,
    DataItemModal,
  },
  
  data() {
    return {
      faqs: [],
      dataItems: [],
      faqCategories: [],
      itemCategories: [],
      businessFaqCategories: [],
      businessDataCategories: [],
      isLoading: false,
      showFaqModal: false,
      showItemModal: false,
      editingFaq: null,
      editingItem: null,
      stats: {
        totalFaqs: 0,
        totalDataItems: 0,
        activeFaqs: 0,
        activeDataItems: 0,
      },
      // Pagination states
      faqPagination: {
        current_page: 1,
        per_page: 20,
        total_count: 0,
        total_pages: 0,
        has_next_page: false,
        has_prev_page: false
      },
      itemPagination: {
        current_page: 1,
        per_page: 20,
        total_count: 0,
        total_pages: 0,
        has_next_page: false,
        has_prev_page: false
      },
      // Search and filter states
      faqFilters: {
        search: '',
        category: '',
        status: ''
      },
      itemFilters: {
        search: '',
        category: '',
        status: ''
      },
      isSearching: false
    };
  },
  computed: {
    currentAccountId() {
      return this.$store.getters.getCurrentAccountId;
    },
  },

  watch: {
    currentAccountId(newVal, oldVal) {
      if (newVal && newVal !== oldVal) {
        this.loadData();
      }
    }
  },
  
  mounted() {
    this.loadData();
  },
  
  methods: {
    async loadData() {
      if (!this.currentAccountId) {
        this.isLoading = false;
        return;
      }
      this.isLoading = true;
      try {
        await Promise.all([
          this.loadFaqs(),
          this.loadDataItems(),
          this.loadBusinessCategories()
        ]);
        this.loadStats();
      } catch (error) {
        useAlert(this.$t('BUSINESS_DATA.ERROR.LOAD_FAILED'), 'error');
      } finally {
        this.isLoading = false;
      }
    },
    
    async loadFaqs() {
      try {
        const params = {
          page: this.faqPagination.current_page,
          per_page: this.faqPagination.per_page,
          ...this.faqFilters
        };
        
        const response = await window.axios.get(`/api/v1/accounts/${this.currentAccountId}/business_faqs`, { params });
        this.faqs = response.data.faqs || [];
        this.faqCategories = response.data.meta?.categories || [];
        this.faqPagination = {
          current_page: response.data.meta.current_page,
          per_page: response.data.meta.per_page,
          total_count: response.data.meta.total_count,
          total_pages: response.data.meta.total_pages,
          has_next_page: response.data.meta.has_next_page,
          has_prev_page: response.data.meta.has_prev_page
        };
      } catch (error) {
        console.error('Error loading FAQs:', error);
      }
    },
    
    async loadDataItems() {
      try {
        const params = {
          page: this.itemPagination.current_page,
          per_page: this.itemPagination.per_page,
          ...this.itemFilters
        };
        
        const response = await window.axios.get(`/api/v1/accounts/${this.currentAccountId}/business_data_items`, { params });
        this.dataItems = response.data.data_items || [];
        this.itemCategories = response.data.meta?.categories || [];
        this.itemPagination = {
          current_page: response.data.meta.current_page,
          per_page: response.data.meta.per_page,
          total_count: response.data.meta.total_count,
          total_pages: response.data.meta.total_pages,
          has_next_page: response.data.meta.has_next_page,
          has_prev_page: response.data.meta.has_prev_page
        };
      } catch (error) {
        console.error('Error loading data items:', error);
      }
    },
    
    async loadBusinessCategories() {
      try {
        const response = await window.axios.get(`/api/v1/accounts/${this.currentAccountId}/business_categories`);
        const categories = response.data.business_categories || [];
        
        console.log('Loaded business categories:', categories);
        
        this.businessFaqCategories = categories.filter(cat => cat.type === 'faq');
        this.businessDataCategories = categories.filter(cat => cat.type === 'data');
        
        console.log('FAQ Categories:', this.businessFaqCategories);
        console.log('Data Categories:', this.businessDataCategories);
      } catch (error) {
        console.error('Error loading business categories:', error);
      }
    },
    
    loadStats() {
      this.stats = {
        totalFaqs: this.faqPagination.total_count,
        totalDataItems: this.itemPagination.total_count,
        activeFaqs: this.faqs.filter(faq => faq.is_active).length,
        activeDataItems: this.dataItems.filter(item => item.is_active).length,
      };
    },
    
    // FAQ pagination and search methods
    onFaqSearch(searchQuery) {
      this.faqFilters.search = searchQuery;
      this.faqPagination.current_page = 1; // Reset to first page
      this.loadFaqs();
    },
    
    onFaqFilterChange(filters) {
      this.faqFilters.category = filters.category;
      this.faqFilters.status = filters.status;
      this.faqPagination.current_page = 1; // Reset to first page
      this.loadFaqs();
    },
    
    onFaqPageChange(page) {
      this.faqPagination.current_page = page;
      this.loadFaqs();
    },
    
    // Data Items pagination and search methods
    onItemSearch(searchQuery) {
      this.itemFilters.search = searchQuery;
      this.itemPagination.current_page = 1; // Reset to first page
      this.loadDataItems();
    },
    
    onItemFilterChange(filters) {
      this.itemFilters.category = filters.category;
      this.itemFilters.status = filters.status;
      this.itemPagination.current_page = 1; // Reset to first page
      this.loadDataItems();
    },
    
    onItemPageChange(page) {
      this.itemPagination.current_page = page;
      this.loadDataItems();
    },
    
    editFaq(faq) {
      this.editingFaq = { ...faq };
      this.showFaqModal = true;
    },
    
    editDataItem(item) {
      this.editingItem = { ...item };
      this.showItemModal = true;
    },
    
    closeFaqModal() {
      this.showFaqModal = false;
      this.editingFaq = null;
    },
    
    closeItemModal() {
      this.showItemModal = false;
      this.editingItem = null;
    },
    
    async onFaqSaved() {
      const wasEditing = !!this.editingFaq;
      this.closeFaqModal();
      await this.loadFaqs();
      this.loadStats();
      const message = wasEditing 
        ? this.$t('BUSINESS_DATA.FAQ.UPDATED_SUCCESS')
        : this.$t('BUSINESS_DATA.FAQ.ADDED_SUCCESS');
      useAlert(message, 'success');
    },
    
    async onItemSaved() {
      const wasEditing = !!this.editingItem;
      this.closeItemModal();
      await this.loadDataItems();
      this.loadStats();
      const message = wasEditing 
        ? this.$t('BUSINESS_DATA.ITEMS.UPDATED_SUCCESS')
        : this.$t('BUSINESS_DATA.ITEMS.ADDED_SUCCESS');
      useAlert(message, 'success');
    },
    
    onCategoryCreated(newCategory) {
      // Add new category to appropriate list
      if (newCategory.type === 'faq') {
        this.businessFaqCategories.push(newCategory);
      } else if (newCategory.type === 'data') {
        this.businessDataCategories.push(newCategory);
      }
    },

  },
};
</script>
